import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabaseClient';
import { FloatingCurrency } from './components/layout/FloatingCurrency';
import { BottomNav } from './components/layout/BottomNav';
import { CardCountSummary } from './components/inventory/CardCountSummary';
import { CardInventoryGrid } from './components/inventory/CardInventoryGrid';
import { DailyActionsPanel } from './components/actions/DailyActionsPanel';
import { GachaMachine3D } from './components/gacha/GachaMachine3D';
import { SplashScreen } from './components/auth/SplashScreen';
import { AuthErrorDialog } from './components/auth/AuthErrorDialog';
import { RankingModal } from './components/rankings/RankingModal';
import { DeckManager } from './components/decks/DeckManager';
import { GameMatchModal } from './components/game/GameMatchModal';
import { GameBoard } from './components/game/GameBoard';
import { useCardInventory } from './hooks/useInventory';
import { useProfile } from './hooks/useProfile';
import { useAuthError } from './contexts/AuthErrorContext';
import { useCurrencyVisibility } from './contexts/CurrencyVisibilityContext';
import { WelcomeBonusModal } from './components/auth/WelcomeBonusModal';
import { acknowledgeWelcomeBonus } from './api/profile';
import type { UserProfile } from './types/profile';
import './App.css';
import './components/inventory/InventoryTabs.css';

const MIN_SPLASH_DISPLAY_MS = 800;
const SPLASH_TIPS = [
  '필드에 카드가 없다면 직접 공격으로 빠르게 마무리해보세요.',
  '스킬 사용 전에 마나를 확인하면 실수를 줄일 수 있어요.',
  'DSM-TCG 후원은 서버 운영비와 콘텐츠 제작에 사용됩니다.',
  '턴 종료 전에 남은 카드 효과를 확인하는 습관을 들여보세요.',
];

type TabType = 'home' | 'gacha' | 'inventory';
type InventorySubTab = 'cards' | 'decks';

function App() {
  const hasSupabaseConfig =
    Boolean(process.env.REACT_APP_SUPABASE_URL) &&
    Boolean(process.env.REACT_APP_SUPABASE_ANON_KEY);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('home');
  const [inventorySubTab, setInventorySubTab] = useState<InventorySubTab>('cards');
  const [isRankingModalOpen, setIsRankingModalOpen] = useState(false);
  const [isGameMatchModalOpen, setIsGameMatchModalOpen] = useState(false);
  const [currentGameSessionId, setCurrentGameSessionId] = useState<string | null>(null);
  const [isSplashVisible, setIsSplashVisible] = useState(true);
  const [splashStartTime, setSplashStartTime] = useState<number | null>(null);
  const splashHideTimeoutRef = useRef<number | null>(null);
  const [isWelcomeBonusModalOpen, setIsWelcomeBonusModalOpen] = useState(false);
  const [isAcknowledgingWelcomeBonus, setIsAcknowledgingWelcomeBonus] = useState(false);
  const { showAuthError, hideAuthError, isAuthErrorVisible } = useAuthError();
  const { showCurrency } = useCurrencyVisibility();
  const { data: profile, isLoading: isProfileLoading } = useProfile();
  const {
    data: inventory,
    isLoading: isInventoryLoading,
    error: inventoryError,
    refetch: refetchInventory,
  } = useCardInventory(Boolean(session));
  const prevCurrencyRef = useRef<number | undefined>(profile?.currency);
  const queryClient = useQueryClient();

  const isAuthenticated = Boolean(session);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (isMounted) {
        setSession(data.session);
        setSessionChecked(true);
      }
    });

    const {
      data: authListener,
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (!sessionChecked) {
        setSessionChecked(true);
      }
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [sessionChecked]);

  // 401 에러 이벤트 리스너
  useEffect(() => {
    const handle401Error = async () => {
      // 세션 초기화
      await supabase.auth.signOut();
      setSession(null);
      // 다이얼로그 표시
      showAuthError();
    };

    window.addEventListener('auth-error-401', handle401Error);

    return () => {
      window.removeEventListener('auth-error-401', handle401Error);
    };
  }, [showAuthError]);

  // 바텀 네비게이션 변경 감지
  useEffect(() => {
    if (isAuthenticated) {
      showCurrency();
    }
  }, [activeTab, isAuthenticated, showCurrency]);

  // 재화 변경 감지 (초기 로드 제외)
  useEffect(() => {
    if (
      prevCurrencyRef.current !== undefined &&
      profile?.currency !== undefined &&
      prevCurrencyRef.current !== profile?.currency
    ) {
      showCurrency();
    }
    prevCurrencyRef.current = profile?.currency;
  }, [profile?.currency, showCurrency]);

  // 환영 보너스 안내 모달 표시
  useEffect(() => {
    if (
      isAuthenticated &&
      !isProfileLoading &&
      profile &&
      profile.welcome_bonus_seen === false &&
      !isSplashVisible
    ) {
      setIsWelcomeBonusModalOpen(true);
    } else if (
      (!isAuthenticated || profile?.welcome_bonus_seen) &&
      isWelcomeBonusModalOpen
    ) {
      setIsWelcomeBonusModalOpen(false);
    }
  }, [
    isAuthenticated,
    isProfileLoading,
    profile,
    profile?.welcome_bonus_seen,
    isSplashVisible,
    isWelcomeBonusModalOpen,
  ]);

  // 다이얼로그 확인 버튼 클릭 시 스플래시로 리다이렉트
  const handleAuthErrorConfirm = () => {
    hideAuthError();
    setSessionChecked(false);
    setIsSplashVisible(true);
    setSplashStartTime(null);
  };

  const breakdown = useMemo(
    () =>
      inventory?.card_count ?? {
        ssgrade_card_count: 0,
        sgrade_card_count: 0,
        agrade_card_count: 0,
        bgrade_card_count: 0,
        cgrade_card_count: 0,
        dgrade_card_count: 0,
      },
    [inventory]
  );

  const totalCards = inventory?.total_count ?? 0;
  const cards = inventory?.card_list ?? [];

  // 로딩 진행률 계산
  const calculateProgress = (): number => {
    if (!sessionChecked) return 0; // 세션 체크 중
    if (!isAuthenticated) return 0; // 로그인 안됨
    
    let progress = 10; // 세션 체크 완료
    
    if (!isProfileLoading && profile !== undefined) {
      progress += 50; // 프로필 로딩 완료
    }
    
    if (!isInventoryLoading && inventory !== undefined) {
      progress += 40; // 인벤토리 로딩 완료
    }
    
    return Math.min(progress, 100);
  };

  const loadingProgress = calculateProgress();
  const splashTip = useMemo(
    () => SPLASH_TIPS[Math.floor(Math.random() * SPLASH_TIPS.length)],
    []
  );

  const isInitialDataReady = useMemo(() => {
    return (
      isAuthenticated &&
      !isProfileLoading &&
      profile !== undefined &&
      !isInventoryLoading &&
      inventory !== undefined
    );
  }, [isAuthenticated, isProfileLoading, profile, isInventoryLoading, inventory]);

  const splashStatus = useMemo(() => {
    if (!sessionChecked) {
      return {
        title: '세션 확인 중',
        subtitle: '안전한 접속을 위해 신원을 확인하고 있어요.',
      };
    }

    if (!isAuthenticated) {
      return {
        title: '길드 초대 준비 완료',
        subtitle: 'GitHub 계정으로 로그인하면 전장에 입장할 수 있어요.',
      };
    }

    if (isProfileLoading || profile === undefined) {
      return {
        title: '플레이어 데이터 동기화',
        subtitle: '프로필 정보를 불러오는 중입니다.',
      };
    }

    if (isInventoryLoading || inventory === undefined) {
      return {
        title: '카드 인벤토리 정리',
        subtitle: '보유한 카드를 전장에서 사용할 수 있게 정돈하고 있어요.',
      };
    }

    return {
      title: '전장 준비 완료',
      subtitle: '모든 자원이 준비됐어요. 곧 메인 화면으로 이동합니다.',
    };
  }, [
    sessionChecked,
    isAuthenticated,
    isProfileLoading,
    profile,
    isInventoryLoading,
    inventory,
  ]);

  // 스플래시 최소 표시 시간 & 전환 제어
  useEffect(() => {
    return () => {
      if (splashHideTimeoutRef.current) {
        window.clearTimeout(splashHideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const needsToShowSplash =
      !sessionChecked || !isInitialDataReady || loadingProgress < 100;

    if (needsToShowSplash) {
      if (!isSplashVisible) {
        setIsSplashVisible(true);
      }
      if (splashStartTime === null) {
        setSplashStartTime(Date.now());
      }
      if (splashHideTimeoutRef.current) {
        window.clearTimeout(splashHideTimeoutRef.current);
        splashHideTimeoutRef.current = null;
      }
      return;
    }

    if (!isSplashVisible) {
      return;
    }

    const elapsed = splashStartTime ? Date.now() - splashStartTime : 0;
    const remaining = Math.max(MIN_SPLASH_DISPLAY_MS - elapsed, 0);

    if (splashHideTimeoutRef.current) {
      window.clearTimeout(splashHideTimeoutRef.current);
    }

    splashHideTimeoutRef.current = window.setTimeout(() => {
      setIsSplashVisible(false);
      setSplashStartTime(null);
      splashHideTimeoutRef.current = null;
    }, remaining);
  }, [
    sessionChecked,
    isInitialDataReady,
    loadingProgress,
    isSplashVisible,
    splashStartTime,
  ]);

  if (!hasSupabaseConfig) {
    return (
      <div className="fallback-container">
        <h1>Supabase 환경 설정이 필요합니다</h1>
        <p>
          <code>REACT_APP_SUPABASE_URL</code>과{' '}
          <code>REACT_APP_SUPABASE_ANON_KEY</code> 환경 변수를 설정한 뒤 앱을
          다시 시작해주세요.
        </p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
  return (
      <DailyActionsPanel
        isAuthenticated={isAuthenticated}
        currency={profile?.currency}
        onGameStart={() => setIsGameMatchModalOpen(true)}
        onRankingClick={() => setIsRankingModalOpen(true)}
      />
        );
      case 'gacha':
        return (
      <GachaMachine3D
        isAuthenticated={isAuthenticated}
        currency={profile?.currency}
      />
        );
      case 'inventory':
        return (
          <>
            {/* 인벤토리 서브 탭 */}
            <div className="inventory-tabs">
              <button
                className={`inventory-tabs__tab ${inventorySubTab === 'cards' ? 'active' : ''}`}
                onClick={() => setInventorySubTab('cards')}
              >
                카드
              </button>
              <button
                className={`inventory-tabs__tab ${inventorySubTab === 'decks' ? 'active' : ''}`}
                onClick={() => setInventorySubTab('decks')}
              >
                덱
              </button>
            </div>

            {inventorySubTab === 'cards' ? (
              <>
                {inventoryError ? (
                  <div className="error-card">
                    <p>카드 정보를 불러올 수 없습니다.</p>
                    <button
                      className="error-card__retry-button"
                      onClick={() => refetchInventory()}
                    >
                      다시 시도
                    </button>
                  </div>
                ) : (
                  <>
                    <CardCountSummary breakdown={breakdown} total={totalCards} />
                    <CardInventoryGrid cards={cards} isLoading={isInventoryLoading} />
                  </>
                )}
              </>
            ) : (
              <DeckManager cards={cards} />
            )}
          </>
        );
      default:
        return null;
    }
  };

  // 스플래시 화면 표시 조건
  if (isSplashVisible) {
    return (
      <>
        <SplashScreen
          isAuthenticated={isAuthenticated}
          canAttemptLogin={sessionChecked && !isAuthenticated}
          progress={loadingProgress}
          statusTitle={splashStatus.title}
          statusSubtitle={splashStatus.subtitle}
          tip={splashTip}
        />
        <AuthErrorDialog
          isOpen={isAuthErrorVisible}
          onConfirm={handleAuthErrorConfirm}
        />
      </>
    );
  }

  // 게임이 진행 중이면 GameBoard 표시
  if (currentGameSessionId) {
    return (
      <GameBoard
        gameSessionId={currentGameSessionId}
        onExit={() => {
          setCurrentGameSessionId(null);
          setIsGameMatchModalOpen(false);
        }}
      />
    );
  }

  return (
    <>
      <div className="game-app">
        <FloatingCurrency currency={profile?.currency} />
        <main className="game-app__content">
          {renderContent()}
        </main>
        <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
      <GameMatchModal
        isOpen={isGameMatchModalOpen}
        onClose={() => setIsGameMatchModalOpen(false)}
        onGameStart={(gameSessionId) => {
          setCurrentGameSessionId(gameSessionId);
          setIsGameMatchModalOpen(false);
        }}
      />
      <RankingModal
        isOpen={isRankingModalOpen}
        onClose={() => setIsRankingModalOpen(false)}
      />
      <AuthErrorDialog
        isOpen={isAuthErrorVisible}
        onConfirm={handleAuthErrorConfirm}
      />
      <WelcomeBonusModal
        isOpen={isWelcomeBonusModalOpen}
        isProcessing={isAcknowledgingWelcomeBonus}
        onConfirm={async () => {
          if (isAcknowledgingWelcomeBonus) return;
          setIsAcknowledgingWelcomeBonus(true);
          try {
            await acknowledgeWelcomeBonus();
            queryClient.setQueryData<UserProfile | null>(['profile'], (prev) =>
              prev ? { ...prev, welcome_bonus_seen: true } : prev
            );
          } catch (error) {
            console.error('Failed to acknowledge welcome bonus modal', error);
          } finally {
            setIsAcknowledgingWelcomeBonus(false);
            setIsWelcomeBonusModalOpen(false);
          }
        }}
      />
    </>
  );
}

export default App;
